import axios from 'axios';
import { authService } from './authService';

// Create axios instance with custom config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 300000, // 5 minute timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Get the current API configuration
export const getCurrentApiConfig = () => {
  return {
    baseURL: api.defaults.baseURL,
    timeout: api.defaults.timeout
  };
};

// Request Interceptor: Attach JWT Token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 Unauthorized
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // If the error is an authorization failure, log the user out
    if (error.response && error.response.status === 401) {
      console.warn("Unauthorized request, logging out.");
      authService.logout();
    }
    return Promise.reject(error);
  }
);

// Set the API configuration (kept for compatibility)
export const setMongoConfig = (config) => {
  // Config is now handled by environment variables
};

// Helper function to extract error messages properly
const extractErrorMessage = (error, defaultMessage) => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') {
      return detail;
    } else if (Array.isArray(detail)) {
      // FastAPI validation errors are often arrays
      return detail.map(err => err.msg || JSON.stringify(err)).join('; ');
    } else if (typeof detail === 'object') {
      return JSON.stringify(detail);
    }
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

const isNetworkError = (error) => {
  return (
    !error.response &&
    (error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ERR_CONNECTION_REFUSED' ||
      error.message === 'Network Error')
  );
};

export const fetchProjects = async () => {
  try {
    const response = await api.get('/api/appdata');
    return response.data;
  } catch (error) {
    if (isNetworkError(error)) {
      throw new Error('Cannot connect to backend server. Please ensure the backend is running on port 8001.');
    }
    throw new Error(error.message || 'Failed to fetch projects');
  }
};

export const fetchRuns = async () => {
  try {
    const response = await api.get('/api/runs');
    return response.data;
  } catch (error) {
    if (isNetworkError(error)) {
      throw new Error('Cannot connect to backend server. Please ensure the backend is running on port 8001.');
    }
    throw new Error(error.message || 'Failed to fetch runs');
  }
};

export const fetchProjectDetails = async (projectId) => {
  try {
    const response = await api.get(`/api/project/${projectId}`);
    return response.data;
  } catch (error) {
    // Error fetching project details
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching project details. Please try again.');
    }
    throw new Error(error.message || 'Failed to fetch project details');
  }
};

export const fetchProjectGovernanceSummary = async (projectId) => {
  try {
    const response = await api.get(`/api/project/${projectId}/governance-summary`);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching governance summary.');
    }
    throw new Error(error.message || 'Failed to fetch governance summary');
  }
};

export const checkDatabaseStatus = async () => {
  try {
    const response = await api.get('/api/database_status', {
      timeout: 60000
    });
    return response.data;
  } catch (error) {
    // Error checking database status
    if (error.code === 'ECONNABORTED') {
      throw new Error('Connection timed out. Please check your network connection and try again.');
    }
    if (error.response?.status === 401) {
      throw new Error('Invalid database credentials. Please check your configuration.');
    }
    if (error.response?.status === 404) {
      throw new Error('Could not connect to the database server. Please verify the configuration.');
    }
    throw new Error(error.message || 'Failed to connect to database. Please check your configuration.');
  }
};

// Keep old function name for compatibility
export const checkMongoStatus = checkDatabaseStatus;

export const fetchTraces = async (traceIds, projectName, userId) => {
  try {
    const response = await api.post('/api/traces', {
      trace_ids: traceIds,
      project_name: projectName,
      user_id: userId
    });
    return response.data;
  } catch (error) {
    // Error fetching traces
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching traces. Please try again.');
    }
    throw new Error(error.message || 'Failed to fetch traces');
  }
};

export const fetchTracesByRunId = async (runId) => {
  try {
    const response = await api.get(`/api/runs/${runId}/traces`);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching traces. Please try again.');
    }
    throw new Error(error.message || 'Failed to fetch traces for run');
  }
};

export const getTraceById = async (traceId) => {
  try {
    const response = await api.get(`/api/trace/${traceId}`);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching trace. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to fetch trace'));
  }
};

export const getSpansByTraceId = async (traceId) => {
  try {
    const response = await api.get(`/api/span/${traceId}`);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching spans. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to fetch spans'));
  }
};

export const getAllTraces = async () => {
  try {
    const response = await api.get('/api/traces');
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while fetching all traces. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to fetch all traces'));
  }
};

// ============================================
// MongoDB-specific endpoints
// ============================================

export const saveProjectDataMongoDB = async (projectData, runs, apiKey, projectName, mongoUri = null, dbName = null) => {
  try {
    const requestData = {
      project_data: projectData,
      runs: runs,
      api_key: apiKey,
      project_name: projectName
    };
    if (mongoUri) requestData.mongo_uri = mongoUri;
    if (dbName) requestData.db_name = dbName;

    const response = await api.post('/api/v1/save/mongodb', requestData);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving project data to MongoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save project data to MongoDB'));
  }
};

export const saveTraceMongoDB = async (projectId, traceData, apiKey, mongoUri = null, dbName = null) => {
  try {
    const requestData = {
      project_id: projectId,
      trace_data: traceData,
      api_key: apiKey
    };
    if (mongoUri) requestData.mongo_uri = mongoUri;
    if (dbName) requestData.db_name = dbName;

    const response = await api.post('/api/v1/traces/mongodb', requestData);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving trace to MongoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save trace to MongoDB'));
  }
};

export const saveSpanMongoDB = async (projectId, spanData, apiKey, mongoUri = null, dbName = null) => {
  try {
    const requestData = {
      project_id: projectId,
      span_data: spanData,
      api_key: apiKey
    };
    if (mongoUri) requestData.mongo_uri = mongoUri;
    if (dbName) requestData.db_name = dbName;

    const response = await api.post('/api/v1/spans/mongodb', requestData);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving span to MongoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save span to MongoDB'));
  }
};

export const deleteProjectMongoDB = async (projectId, mongoUri = null, dbName = null) => {
  try {
    const params = {};
    if (mongoUri) params.mongo_uri = mongoUri;
    if (dbName) params.db_name = dbName;

    const response = await api.delete(`/api/v1/projects/${projectId}/mongodb`, { params });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while deleting project from MongoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to delete project from MongoDB'));
  }
};

export const deleteRunMongoDB = async (runId, mongoUri = null, dbName = null) => {
  try {
    const params = {};
    if (mongoUri) params.mongo_uri = mongoUri;
    if (dbName) params.db_name = dbName;

    const response = await api.delete(`/api/v1/runs/${runId}/mongodb`, { params });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while deleting run from MongoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to delete run from MongoDB'));
  }
};

// ============================================
// DynamoDB-specific endpoints
// ============================================

/**
 * Save project data to DynamoDB (unified save: project_data, runs, and optionally traces/spans in one request).
 * SaaS-style: traces and spans are optional; when provided they are stored in {table_name}-traces and {table_name}-spans for consistency.
 */
export const saveProjectDataDynamoDB = async (
  projectData,
  runs,
  apiKey,
  projectName,
  awsRegion = null,
  tableName = null,
  traces = null,
  spans = null
) => {
  try {
    const requestData = {
      project_data: projectData,
      runs: runs,
      api_key: apiKey,
      project_name: projectName
    };
    if (awsRegion) requestData.aws_region = awsRegion;
    if (tableName) requestData.table_name = tableName;
    if (traces != null && Array.isArray(traces)) requestData.traces = traces;
    if (spans != null && Array.isArray(spans)) requestData.spans = spans;

    const response = await api.post('/api/v1/save/dynamodb', requestData);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving project data to DynamoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save project data to DynamoDB'));
  }
};

export const saveTraceDynamoDB = async (projectId, traceData, apiKey, awsRegion = null, tableName = null) => {
  try {
    const requestData = {
      project_id: projectId,
      trace_data: traceData,
      api_key: apiKey
    };
    if (awsRegion) requestData.aws_region = awsRegion;
    if (tableName) requestData.table_name = tableName;

    const response = await api.post('/api/v1/traces/dynamodb', requestData);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving trace to DynamoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save trace to DynamoDB'));
  }
};

export const saveSpanDynamoDB = async (projectId, spanData, apiKey, awsRegion = null, tableName = null) => {
  try {
    const requestData = {
      project_id: projectId,
      span_data: spanData,
      api_key: apiKey
    };
    if (awsRegion) requestData.aws_region = awsRegion;
    if (tableName) requestData.table_name = tableName;

    const response = await api.post('/api/v1/spans/dynamodb', requestData);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving span to DynamoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save span to DynamoDB'));
  }
};

export const deleteProjectDynamoDB = async (projectId, awsRegion = null, tableName = null) => {
  try {
    const params = {};
    if (awsRegion) params.aws_region = awsRegion;
    if (tableName) params.table_name = tableName;

    const response = await api.delete(`/api/v1/projects/${projectId}/dynamodb`, { params });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while deleting project from DynamoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to delete project from DynamoDB'));
  }
};

export const deleteRunDynamoDB = async (runId, awsRegion = null, tableName = null) => {
  try {
    const params = {};
    if (awsRegion) params.aws_region = awsRegion;
    if (tableName) params.table_name = tableName;

    const response = await api.delete(`/api/v1/runs/${runId}/dynamodb`, { params });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while deleting run from DynamoDB. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to delete run from DynamoDB'));
  }
};

// ============================================
// ClickHouse-specific endpoints
// ============================================

export const saveProjectDataClickHouse = async (projectData, runs, apiKey, projectName) => {
  try {
    const response = await api.post('/api/v1/save/clickhouse', {
      project_data: projectData,
      runs: runs,
      api_key: apiKey,
      project_name: projectName
    });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving project data to ClickHouse. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save project data to ClickHouse'));
  }
};

export const saveTraceClickHouse = async (projectId, traceData, apiKey) => {
  try {
    const response = await api.post('/api/v1/traces/clickhouse', {
      project_id: projectId,
      trace_data: traceData,
      api_key: apiKey
    });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving trace to ClickHouse. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save trace to ClickHouse'));
  }
};

export const saveSpanClickHouse = async (projectId, spanData, apiKey) => {
  try {
    const response = await api.post('/api/v1/spans/clickhouse', {
      project_id: projectId,
      span_data: spanData,
      api_key: apiKey
    });
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while saving span to ClickHouse. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to save span to ClickHouse'));
  }
};

export const deleteProjectClickHouse = async (projectId) => {
  try {
    const response = await api.delete(`/api/v1/projects/${projectId}/clickhouse`);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while deleting project from ClickHouse. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to delete project from ClickHouse'));
  }
};

export const deleteRunClickHouse = async (runId) => {
  try {
    const response = await api.delete(`/api/v1/runs/${runId}/clickhouse`);
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while deleting run from ClickHouse. Please try again.');
    }
    throw new Error(extractErrorMessage(error, 'Failed to delete run from ClickHouse'));
  }
};

export const getHealthStatus = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out while checking health status. Please try again.');
    }
    throw new Error(error.message || 'Failed to check health status');
  }
};

// Unified delete functions that try all databases
export const deleteProject = async (projectId) => {
  const errors = [];

  // Try MongoDB
  try {
    const result = await deleteProjectMongoDB(projectId);
    return { success: true, database: 'MongoDB', result };
  } catch (error) {
    errors.push({ database: 'MongoDB', error: error.message });
  }

  // Try DynamoDB
  try {
    const result = await deleteProjectDynamoDB(projectId);
    return { success: true, database: 'DynamoDB', result };
  } catch (error) {
    errors.push({ database: 'DynamoDB', error: error.message });
  }

  // Try ClickHouse
  try {
    const result = await deleteProjectClickHouse(projectId);
    return { success: true, database: 'ClickHouse', result };
  } catch (error) {
    errors.push({ database: 'ClickHouse', error: error.message });
  }

  // If all failed, throw an error with details
  throw new Error(`Failed to delete project from all databases: ${errors.map(e => `${e.database}: ${e.error}`).join('; ')}`);
};

/**
 * Delete a run using the backend's configured database (single endpoint).
 * Backend uses DB_TYPE to route to MongoDB, DynamoDB, or ClickHouse.
 */
export const deleteRun = async (runId) => {
  try {
    const response = await api.delete(`/api/runs/${runId}`);
    return { success: true, ...response.data };
  } catch (error) {
    const message = extractErrorMessage(error, 'Failed to delete run');
    throw new Error(message);
  }
};
